/* eslint-disable qwik/no-use-visible-task */
import { component$, useSignal, useStore, useStyles$, useVisibleTask$ } from "@builder.io/qwik";
import { FancyImage } from "@/common-components/fancy-image/FancyImage";
import { cn } from "@/utils/tailwind-helper";
import type {
    IGenNavigation_Items,
    IGenNavigationCategory,
    IGenPage,
    Maybe,
} from "../../services/graphql/__generated/sdk";
import { useNavigation } from "../../hooks/useNavigation";
import { useUniversalText } from "~/hooks/useCMSData";
import { BREAKPOINTS } from "~/constants/mediaquery";
import { useAnimatedBorder } from "~/hooks/useAnimatedBorder";
import { ButtonSecondary } from "../button/ButtonSecondary";
import { imageZoomChildClass, imageZoomGroupClass } from "~/constants/imageZoomAnimation";

interface NavigationHighlightProps {
    rightSide?: "tripple" | "single";
    highlightCategories?: Maybe<IGenNavigation_Items>[] | undefined;
    categoy?: Maybe<IGenNavigationCategory>;
    willAnimate: boolean;
}

export const NavigationHighlight = component$<NavigationHighlightProps>(
    ({ rightSide = "single", categoy, highlightCategories, willAnimate }) => {
        const leftHeadline = categoy?.name || "";
        const { getLink } = useNavigation();
        const { discover = "Discover", discoverAll = "Discover All", highlighted = "New" } = useUniversalText();
        const hoveringItemId = useSignal<string | null>(null);
        const borderRefs = useStore<{ [key: string]: HTMLElement }>({});
        const currentHighlight = useSignal<IGenPage | null>(null);
        const prevHighlight = useSignal<IGenPage | null>(null);
        const wrapperRef = useSignal<HTMLElement>(null!);
        useStyles$(
            `
                @keyframes fade-in {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }
                
                @keyframes fade-out {
                    from {
                        opacity: 1;
                    }
                    to {
                        opacity: 0;
                    }
                }
            `,
        );

        const { ANIMATION_DURATION, mouseEnterHandler$, mouseLeaveHandler$ } = useAnimatedBorder({
            borderRefs,
            hoveringItemId,
        });

        const highlights: IGenPage[] = [];
        categoy?.items?.forEach((item) => {
            if (highlights.length < 3 && item?.ogImage?.src) {
                highlights.push(item);
            }
        });

        useVisibleTask$(({ track }) => {
            track(() => categoy);
            hoveringItemId.value = categoy?.items?.[0]?.id || null;
            currentHighlight.value = categoy?.items?.[0] || null;
        });

        useVisibleTask$(({ track }) => {
            track(() => willAnimate);
            if (!wrapperRef.value) return;

            if (willAnimate) {
                wrapperRef.value.style.animationName = "fade-out";
                wrapperRef.value.style.animationDuration = `${ANIMATION_DURATION / 2}ms`;
            } else {
                wrapperRef.value.style.animationName = "fade-in";
                wrapperRef.value.style.animationDuration = `${ANIMATION_DURATION / 2}ms`;
            }
        });

        const leftSubtext =
            highlightCategories && highlightCategories.indexOf(categoy!) < 1
                ? currentHighlight.value?.kitchenProgramme?.name
                : categoy?.description;

        const currentDiscoverLink = currentHighlight ? getLink(currentHighlight.value) : {};

        return (
            <div class={cn("hidden bg-white/[0.99] gold:block")}>
                {/* Outer grid: 12 columns from gold up */}
                <div
                    class={cn(
                        "grid w-full grid-cols-12 gap-x-6 px-10 pb-40 pt-16",
                        "min-h-[580px] platinum:mx-auto platinum:max-w-[1440px]",
                        "opacity-0",
                    )}
                    style={{
                        animationFillMode: "forwards",
                        animationTimingFunction: "ease",
                    }}
                    ref={wrapperRef}
                >
                    {/* LEFT BLOCK */}
                    <div class={cn("col-span-2", "flex max-w-[80%] flex-col gap-2")}>
                        {/* Headline + subtext */}
                        <h2
                            class={cn("text-primary-500", "text-[14px] font-medium uppercase leading-5")}
                            dangerouslySetInnerHTML={leftHeadline.replace(/\n/g, "<br />")}
                        />
                        {leftSubtext && (
                            <p
                                key={`item-${categoy?.id}`}
                                class={cn("text-[14px] font-light leading-5 text-primary-500/60", "motion-preset-fade")}
                                style={{ "--motion-duration": `${ANIMATION_DURATION}ms` }}
                                dangerouslySetInnerHTML={leftSubtext.replace(/\n/g, "<br />")}
                            />
                        )}
                    </div>
                    <div class={cn("col-span-3 col-start-3", "flex flex-col gap-3.5")}>
                        {/* Navigation Items/RIGHT BLOCK */}
                        <div class={cn("flex flex-col gap-3.5")}>
                            {categoy?.items?.slice(0, 6)?.map((item) => {
                                if (!item || !item.id) return null;

                                const isActive = item.id === hoveringItemId.value;
                                const link = getLink(item);
                                return (
                                    <div
                                        onMouseEnter$={() => {
                                            mouseEnterHandler$(item!.id!);
                                            if (item.id === prevHighlight.value?.id) {
                                                currentHighlight.value = item;
                                                return;
                                            }
                                            prevHighlight.value = currentHighlight.value;
                                            currentHighlight.value = item;
                                        }}
                                        onMouseLeave$={() => {
                                            mouseLeaveHandler$(item!.id!);
                                        }}
                                        key={item.id}
                                        class={cn("w-full")}
                                    >
                                        <a {...link}>
                                            <div
                                                class={cn(
                                                    "relative flex w-full items-center justify-between gap-2 pb-1",
                                                )}
                                            >
                                                <span
                                                    class={cn("text-[16px] font-normal leading-6 text-primary-500")}
                                                    dangerouslySetInnerHTML={item?.name?.replace(/\n/g, "<br />") || ""}
                                                />
                                                <div
                                                    class={cn(
                                                        "flex h-4 w-4 origin-left items-center justify-center text-primary-500",
                                                        isActive
                                                            ? "translate-x-0 opacity-100 transition-[transform,opacity] ease-[ease]"
                                                            : "-translate-x-1/2 opacity-0",
                                                    )}
                                                    style={{
                                                        transitionDuration: `${ANIMATION_DURATION}ms`,
                                                    }}
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 16 16"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M6 12L10 8L6 4"
                                                            stroke="currentColor"
                                                            stroke-width="1.5"
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                        />
                                                    </svg>
                                                </div>
                                                <div
                                                    ref={(el) => el && (borderRefs[item.id!] = el)}
                                                    class={cn(
                                                        "absolute bottom-0 left-0 h-px w-full scale-x-0 bg-primary-500/80",
                                                        isActive && "scale-x-100",
                                                    )}
                                                    style={{
                                                        animationDuration: `${ANIMATION_DURATION}ms`,
                                                        animationFillMode: "forwards",
                                                        animationTimingFunction: "ease",
                                                    }}
                                                />
                                            </div>
                                        </a>
                                    </div>
                                );
                            })}

                            {categoy?.overviewPage && (
                                <a {...getLink(categoy?.overviewPage)} class={cn("group relative block w-fit pb-1")}>
                                    <span class={cn("text-[16px] leading-6 text-primary-500/60")}>{discoverAll}</span>
                                    <div
                                        class={cn(
                                            "absolute bottom-0 left-0 h-px w-full bg-primary-500/40",
                                            "transition-transform ease-[ease]",
                                            "origin-left scale-x-100 group-hover:origin-right group-hover:scale-x-0",
                                        )}
                                        style={{
                                            transitionDuration: `${ANIMATION_DURATION}ms`,
                                        }}
                                    />
                                </a>
                            )}
                        </div>
                    </div>

                    {/* RIGHT BLOCK */}

                    {rightSide === "single" ? (
                        <div class={cn("col-span-6 col-start-7", "relative")}>
                            {currentHighlight.value?.ogImage?.src && (
                                <div
                                    class={cn(
                                        "relative aspect-[690/388] min-h-[388px] w-full overflow-hidden rounded-lg platinum:aspect-[670/388]",
                                    )}
                                    key={`item-${currentHighlight.value?.id}`}
                                >
                                    <div
                                        class="absolute inset-0 z-20 motion-scale-in-105 motion-opacity-in-50"
                                        style={{ "--motion-duration": `${ANIMATION_DURATION}ms` }}
                                    >
                                        <FancyImage
                                            img={currentHighlight.value.ogImage}
                                            lazyload={false}
                                            responsiveAspectRatio={{
                                                gold: 690 / 388,
                                                platinum: 670 / 388,
                                            }}
                                            responsiveFactor={{
                                                gold: 1 / 2,
                                                platinum: 1 / 2,
                                            }}
                                            responsiveMaxWidth={{
                                                platinum: BREAKPOINTS.PLATINUM / 2,
                                            }}
                                            class={cn("min-h-[388px] rounded-lg")}
                                        />
                                    </div>
                                    {prevHighlight.value?.ogImage?.src && (
                                        <div
                                            class="absolute inset-0 opacity-0 motion-opacity-out-50"
                                            style={{ "--motion-duration": `${ANIMATION_DURATION}ms` }}
                                        >
                                            <FancyImage
                                                img={prevHighlight.value?.ogImage}
                                                lazyload={false}
                                                responsiveAspectRatio={{
                                                    gold: 690 / 388,
                                                    platinum: 670 / 388,
                                                }}
                                                responsiveFactor={{
                                                    gold: 1 / 2,
                                                    platinum: 1 / 2,
                                                }}
                                                responsiveMaxWidth={{
                                                    platinum: BREAKPOINTS.PLATINUM / 2,
                                                }}
                                                class={cn("min-h-[388px] rounded-lg")}
                                            />
                                        </div>
                                    )}

                                    {/* Gradient overlay */}
                                    <div
                                        class={cn("pointer-events-none absolute inset-0 z-30 rounded-lg")}
                                        style={{
                                            background:
                                                "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 50.12%, rgba(0, 0, 0, 0.40) 100%)",
                                        }}
                                    />

                                    {/* Content above gradient */}
                                    <div class={cn("absolute inset-0 z-40 flex flex-col justify-between p-8")}>
                                        <div class="flex w-full items-start">
                                            {/* show this badge only for the first item */}
                                            {categoy?.items?.findIndex((i) => i?.id === currentHighlight.value?.id) ===
                                                0 && (
                                                <div
                                                    class={cn(
                                                        "bg-white/20 text-center backdrop-blur-md",
                                                        "rounded-full px-2.5 py-0.5",
                                                        "text-[10px] font-bold leading-4 text-white",
                                                    )}
                                                >
                                                    {highlighted}
                                                </div>
                                            )}
                                        </div>

                                        {/* Title & Button: sits at bottom in Figma */}
                                        <div class="mt-auto flex items-center justify-between">
                                            <span
                                                class={cn("text-white", "text-[16px] font-normal leading-6")}
                                                dangerouslySetInnerHTML={
                                                    currentHighlight.value?.name?.replace(/\n/g, "<br />") || ""
                                                }
                                            />

                                            {/* "Discover" Button - a secondary button style in Figma */}
                                            <a {...currentDiscoverLink}>
                                                <ButtonSecondary
                                                    label={discover}
                                                    class={cn("border border-white-opacity-20")}
                                                />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div
                            class={cn("col-span-7 col-start-6", "ml-auto flex min-h-[388px] w-full max-w-[90%] gap-5")}
                        >
                            {highlights.map((item, i) => {
                                const hLink = getLink(item);
                                return (
                                    <a
                                        {...hLink}
                                        key={i}
                                        class={cn(
                                            "pointer relative h-full w-full overflow-hidden rounded-lg",
                                            imageZoomGroupClass,
                                        )}
                                    >
                                        {/* If there's an image src, display FancyImage */}
                                        {item.ogImage?.src && (
                                            <FancyImage
                                                img={item.ogImage}
                                                lazyload={false}
                                                responsiveAspectRatio={{
                                                    gold: 214 / 388,
                                                    platinum: 248 / 388,
                                                }}
                                                responsiveFactor={{
                                                    gold: 1 / 3,
                                                    platinum: 1 / 3,
                                                }}
                                                responsiveMaxWidth={{
                                                    platinum: BREAKPOINTS.PLATINUM / 3,
                                                }}
                                                noAspectRatio={true}
                                                class={cn("h-full rounded-lg", imageZoomChildClass)}
                                            />
                                        )}
                                        {/* Gradient overlay */}
                                        <div
                                            class={cn("pointer-events-none absolute inset-0 z-20 rounded-lg")}
                                            style={{
                                                background:
                                                    "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 50%, rgba(0, 0, 0, 0.60) 100%)",
                                            }}
                                        />
                                        {/* from Figma if needed, or text label */}
                                        {item.name && (
                                            <span
                                                class={cn(
                                                    "absolute bottom-4 left-4 z-30 text-base leading-5 text-white",
                                                )}
                                                dangerouslySetInnerHTML={item.name.split("\n").join("<br />")}
                                            />
                                        )}
                                    </a>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    },
);
